import { cs } from '../../lib/currency';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Calendar,
  Clock,
  DollarSign,
  UserCheck,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  Share2,
  Link2 as LinkIcon,
  Grid3X3,
  MapPin,
  UserPlus,
  UserMinus,
  Copy,
  ArrowUpDown,
  Eye,
  RotateCcw,
  RefreshCw,
  TicketPercent,
} from 'lucide-react';
import api from '../../services/api';
import RefundDialog from '../../components/admin/RefundDialog';
import ManualDiscountDialog from '../../components/admin/ManualDiscountDialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const SmallGroupManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sessions');
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [slots, setSlots] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [coachPricing, setCoachPricing] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [discountInvoice, setDiscountInvoice] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Dialogs
  const [sessionDialog, setSessionDialog] = useState(false);
  const [slotDialog, setSlotDialog] = useState(false);
  const [pricingDialog, setPricingDialog] = useState(false);
  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Form states
  const [sessionForm, setSessionForm] = useState({
    name: '',
    coach_id: '',
    description: '',
    recurring_day: '',
    default_start_time: '',
    default_duration_hours: 2,
    max_capacity: 10,
    location_id: ''
  });
  
  const [slotForm, setSlotForm] = useState({
    session_id: '',
    date: '',
    start_time: '',
    end_time: '',
    max_capacity: 10,
    lane_id: '',
    notes: ''
  });
  
  const [pricingGrid, setPricingGrid] = useState([]);
  const [selectedCoachForPricing, setSelectedCoachForPricing] = useState('');
  
  // Attendance tracking
  const [attendanceData, setAttendanceData] = useState([]);
  
  // Add Player to Slot — supports adding multiple players at once.
  const [addPlayerDialog, setAddPlayerDialog] = useState(false);
  const [addPlayerSlot, setAddPlayerSlot] = useState(null);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [searchedPlayers, setSearchedPlayers] = useState([]);
  const [selectedPlayersToAdd, setSelectedPlayersToAdd] = useState([]);
  const [addingPlayer, setAddingPlayer] = useState(false);
  
  // Complete Slot Confirmation Dialog
  const [completeConfirmDialog, setCompleteConfirmDialog] = useState(false);
  const [completeSlotData, setCompleteSlotData] = useState(null);
  const [completeConfirmMessage, setCompleteConfirmMessage] = useState('');
  const [completingSlot, setCompletingSlot] = useState(false);
  
  // Post-Attendance Prompt Dialog
  const [postAttendanceDialog, setPostAttendanceDialog] = useState(false);
  const [postAttendanceCount, setPostAttendanceCount] = useState(0);
  const [postAttendanceSlot, setPostAttendanceSlot] = useState(null);
  
  // Reopen Slot Confirmation Dialog
  const [reopenConfirmDialog, setReopenConfirmDialog] = useState(false);
  const [reopenSlotData, setReopenSlotData] = useState(null);
  const [reopeningSlot, setReopeningSlot] = useState(false);
  
  // Delete Confirmation Dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'session' or 'slot'
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Manage Players Dialog
  const [managePlayersDialog, setManagePlayersDialog] = useState(false);
  const [managePlayersSlot, setManagePlayersSlot] = useState(null);
  const [removingPlayer, setRemovingPlayer] = useState(null);

  // Refund dialog (uses reusable RefundDialog component)
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReg, setRefundReg] = useState(null);
  
  // Remove Attended Player Confirmation Dialog
  const [removeAttendedConfirmDialog, setRemoveAttendedConfirmDialog] = useState(false);
  const [playerToRemove, setPlayerToRemove] = useState(null);
  
  // Filters
  const [sessionFilter, setSessionFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Sorting for slots table — default to latest first (date desc, then time desc)
  // so admins see today's & upcoming sessions at the top instead of having to
  // scroll past months of historical sessions.
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const loadedOnce = useRef(false);
  const fetchData = useCallback(async () => {
    if (!loadedOnce.current) setLoading(true);  // later refreshes keep the page visible
    try {
      const [sessionsRes, slotsRes, coachesRes, pricingRes, invoicesRes, lanesRes, locationsRes] = await Promise.all([
        api.get('/small-group/sessions'),
        api.get('/small-group/slots?include_registrations=true'),
        api.get('/coaches'),
        api.get('/small-group/pricing'),
        api.get('/small-group/invoices'),
        api.get('/lanes/config').catch(() => ({ data: [] })),
        api.get('/locations/').catch(() => ({ data: [] }))
      ]);
      
      setSessions(sessionsRes.data);
      setSlots(slotsRes.data);
      setCoaches(coachesRes.data || []);
      setCoachPricing(pricingRes.data || []);
      setInvoices(invoicesRes.data || []);
      setLocations(locationsRes.data || []);
      
      setLanes(lanesRes.data?.lanes || []);
      loadedOnce.current = true;
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Background refresh of the slot list only (no full-page spinner).
  const refreshSlots = useCallback(() => {
    api.get('/small-group/slots?include_registrations=true').then((r) => setSlots(r.data)).catch(() => {});
  }, []);

  // Session CRUD
  const handleSaveSession = async () => {
    try {
      if (editingSession) {
        await api.put(`/small-group/sessions/${editingSession.id}`, sessionForm);
        toast.success('Session updated');
      } else {
        await api.post('/small-group/sessions', sessionForm);
        toast.success('Session created');
      }
      setSessionDialog(false);
      resetSessionForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session');
    }
  };

  const handleDeleteSession = (id, name) => {
    setDeleteType('session');
    setDeleteItemId(id);
    setDeleteItemName(name || 'this session');
    setDeleteConfirmDialog(true);
  };

  const handleDeleteSlot = (slot) => {
    setDeleteType('slot');
    setDeleteItemId(slot.id);
    setDeleteItemName(`${slot.session_name} on ${slot.date}`);
    setDeleteConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;
    
    setDeleting(true);
    try {
      if (deleteType === 'session') {
        await api.delete(`/small-group/sessions/${deleteItemId}`);
        toast.success('Session deleted');
      } else if (deleteType === 'slot') {
        await api.delete(`/small-group/slots/${deleteItemId}`);
        toast.success('Slot deleted');
      }
      setDeleteConfirmDialog(false);
      setDeleteItemId(null);
      setDeleteType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${deleteType}`);
    } finally {
      setDeleting(false);
    }
  };

  const resetSessionForm = () => {
    setSessionForm({
      name: '',
      coach_id: '',
      description: '',
      recurring_day: '',
      default_start_time: '',
      default_duration_hours: 2,
      max_capacity: 10,
      location_id: ''
    });
    setEditingSession(null);
  };

  // Slot CRUD
  const handleSaveSlot = async () => {
    try {
      if (editingSlot) {
        const res = await api.put(`/small-group/slots/${editingSlot.id}`, slotForm);
        setSlots((prev) => prev.map((x) => (x.id === res.data.id ? res.data : x)));
        toast.success('Slot updated');
      } else {
        const res = await api.post('/small-group/slots', slotForm);
        setSlots((prev) => [...prev, { ...res.data, registrations: [] }]);
        toast.success('Slot created');
      }
      setSlotDialog(false);
      resetSlotForm();
      refreshSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save slot');
    }
  };

  const resetSlotForm = () => {
    setSlotForm({
      session_id: '',
      date: '',
      start_time: '',
      end_time: '',
      max_capacity: 10,
      lane_id: '', lane_ids: [],
      notes: ''
    });
    setEditingSlot(null);
  };

  // Pricing Grid
  const handleSavePricing = async () => {
    if (!selectedCoachForPricing) {
      toast.error('Please select a coach');
      return;
    }
    
    try {
      const existingPricing = coachPricing.find(p => p.coach_id === selectedCoachForPricing);
      
      if (existingPricing) {
        await api.put(`/small-group/pricing/${selectedCoachForPricing}`, {
          pricing_grid: pricingGrid
        });
        toast.success('Pricing updated');
      } else {
        await api.post('/small-group/pricing', {
          coach_id: selectedCoachForPricing,
          pricing_grid: pricingGrid
        });
        toast.success('Pricing created');
      }
      setPricingDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    }
  };

  const addPricingRow = () => {
    setPricingGrid([...pricingGrid, { num_kids: 1, duration_hours: 1, rate_per_child: 0 }]);
  };

  const updatePricingRow = (index, field, value) => {
    const newGrid = [...pricingGrid];
    // iter306af — `duration_hours` is now a float (0.5 / 1.0 / 1.5)
    // so half-hour grids work. Was previously parseInt which truncated
    // 1.5 → 1 and silently dropped the user's input.
    if (field === 'num_kids') {
      newGrid[index][field] = parseInt(value) || 0;
    } else {
      newGrid[index][field] = parseFloat(value) || 0;
    }
    setPricingGrid(newGrid);
  };

  const removePricingRow = (index) => {
    setPricingGrid(pricingGrid.filter((_, i) => i !== index));
  };

  // Attendance
  const openAttendanceDialog = async (slot) => {
    setSelectedSlot(slot);
    // Defensive filter — backend now excludes cancelled regs but a
    // stale prop (e.g. from a list cached before the latest fetch)
    // could still leak removed players into the dialog. Belt-and-suspenders.
    const registrations = (slot.registrations || []).filter(
      (r) => r.status !== 'cancelled'
    );
    setAttendanceData(registrations.map(r => ({
      registration_id: r.id,
      player_name: r.player_name,
      parent_name: r.parent_name,
      attended: r.attended || false,
      status: r.status
    })));
    setAttendanceDialog(true);
  };

  const handleSaveAttendance = async () => {
    try {
      await api.post(`/small-group/slots/${selectedSlot.id}/attendance`, {
        attendance: attendanceData.map(a => ({
          registration_id: a.registration_id,
          attended: a.attended
        }))
      });
      
      const attendedCount = attendanceData.filter(a => a.attended).length;
      toast.success(`Attendance saved. ${attendedCount} player(s) marked as attended.`);
      setAttendanceDialog(false);
      
      // Prompt to complete slot and generate invoices if there are attendees
      if (attendedCount > 0 && selectedSlot.status !== 'completed') {
        setPostAttendanceCount(attendedCount);
        setPostAttendanceSlot(selectedSlot);
        setPostAttendanceDialog(true);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    }
  };

  const handlePostAttendanceComplete = async () => {
    if (!postAttendanceSlot) return;
    
    setCompletingSlot(true);
    try {
      const res = await api.post(
        `/small-group/slots/${postAttendanceSlot.id}/complete?generate_invoices=true&send_emails=true`
      );
      toast.success(`Slot completed! ${res.data.invoices_created} invoice(s) generated.`);
      setPostAttendanceDialog(false);
      setPostAttendanceSlot(null);
      fetchData();
    } catch (completeError) {
      toast.error(completeError.response?.data?.detail || 'Failed to complete slot');
    } finally {
      setCompletingSlot(false);
    }
  };

  const handleCompleteSlot = async (slot) => {
    // If there are attendees, check pricing grid first
    if (slot.attended_count > 0) {
      // Get the session to find the coach
      const session = sessions.find(s => s.id === slot.session_id);
      if (!session) {
        toast.error('Session not found');
        return;
      }
      
      // Calculate duration in hours
      const parseTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours + minutes / 60;
      };
      const startHours = parseTime(slot.start_time);
      const endHours = parseTime(slot.end_time);
      // iter306af — round to nearest half-hour so a 1h45m slot matches
      // the coach's 2.0 row (closer than 1.5). `Math.round(x * 2) / 2`
      // is the canonical half-step rounding idiom.
      const durationHours = Math.round((endHours - startHours) * 2) / 2;

      // Check if pricing grid exists for this combination
      try {
        const pricingRes = await api.get(`/small-group/pricing/${session.coach_id}`);
        const pricingGrid = pricingRes.data?.pricing_grid || [];

        // Find matching pricing entry (float compare to be safe with
        // legacy integer-stored rows).
        const matchingPrice = pricingGrid.find(
          p => p.num_kids === slot.attended_count && parseFloat(p.duration_hours) === durationHours
        );
        
        if (!matchingPrice) {
          toast.error(
            `No pricing found for ${slot.attended_count} kid(s) and ${durationHours} hour(s). ` +
            `Please add this combination to the Coach Pricing grid first, then mark complete.`
          );
          return;
        }
      } catch (error) {
        // If no pricing grid exists at all for this coach
        toast.error(
          `No pricing grid found for this coach. ` +
          `Please set up pricing in the Coach Pricing tab first.`
        );
        return;
      }
    }
    
    // Set up confirmation dialog
    let message = '';
    if (slot.attended_count === 0) {
      message = 'No attendance has been marked for this slot. Are you sure you want to complete it without any attendees? No invoices will be generated.';
    } else if (slot.attended_count < slot.registered_count) {
      message = `Only ${slot.attended_count} of ${slot.registered_count} registered players attended. Mark as completed and generate invoices for those who attended?`;
    } else {
      message = 'Mark this slot as completed and generate invoices?';
    }
    
    setCompleteSlotData(slot);
    setCompleteConfirmMessage(message);
    setCompleteConfirmDialog(true);
  };

  const confirmCompleteSlot = async () => {
    if (!completeSlotData) return;
    
    setCompletingSlot(true);
    try {
      const res = await api.post(`/small-group/slots/${completeSlotData.id}/complete?generate_invoices=true&send_emails=true`);
      toast.success(`Slot completed. ${res.data.invoices_created} invoices created.`);
      setCompleteConfirmDialog(false);
      setCompleteSlotData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete slot');
    } finally {
      setCompletingSlot(false);
    }
  };

  // Reopen Completed Slot (Admin Only) - Open confirmation dialog
  const handleReopenSlot = (slot) => {
    setReopenSlotData(slot);
    setReopenConfirmDialog(true);
  };

  // Confirm reopen slot action
  const confirmReopenSlot = async () => {
    if (!reopenSlotData) return;
    
    setReopeningSlot(true);
    try {
      const res = await api.post(`/small-group/slots/${reopenSlotData.id}/reopen`);
      toast.success(`Slot reopened. ${res.data.deleted_pending_invoices || 0} pending invoices removed.`);
      setReopenConfirmDialog(false);
      setReopenSlotData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen slot');
    } finally {
      setReopeningSlot(false);
    }
  };

  // Copy Slot Function
  const handleCopySlot = (slot) => {
    setEditingSlot(null); // Ensure we're creating new, not editing
    setSlotForm({
      session_id: slot.session_id,
      date: '', // Leave date empty for admin to fill
      start_time: slot.start_time,
      end_time: slot.end_time,
      max_capacity: slot.max_capacity,
      lane_id: slot.lane_id || '', lane_ids: slot.lane_ids || (slot.lane_id ? [slot.lane_id] : []),
      notes: slot.notes || ''
    });
    setSlotDialog(true);
    toast.info('Slot copied! Update the date and save.');
  };

  // Add Player to Slot Functions
  const openAddPlayerDialog = (slot) => {
    setAddPlayerSlot(slot);
    setPlayerSearchQuery('');
    setSearchedPlayers([]);
    setSelectedPlayersToAdd([]);
    setAddPlayerDialog(true);
  };

  const togglePlayerToAdd = (attendee) => {
    setSelectedPlayersToAdd((prev) => {
      const exists = prev.some((p) => p.id === attendee.id && p.type === attendee.type);
      if (exists) return prev.filter((p) => !(p.id === attendee.id && p.type === attendee.type));
      return [...prev, attendee];
    });
  };

  const searchPlayers = async (query) => {
    if (!query || query.length < 2) {
      setSearchedPlayers([]);
      return;
    }
    try {
      // iter317 — use the unified attendee search so admins can also pick
      // parents / customers / staff as self-attendees (not just kids).
      const res = await api.get('/small-group/attendee-search', {
        params: { q: query, slot_id: addPlayerSlot?.id, limit: 20 },
      });
      setSearchedPlayers(res.data?.results || []);
    } catch (error) {
      console.error('Failed to search attendees:', error);
      setSearchedPlayers([]);
    }
  };

  const handleAddPlayerToSlot = async () => {
    if (selectedPlayersToAdd.length === 0 || !addPlayerSlot) return;

    // Capacity guard — block if adding more than the remaining spots so we
    // don't half-add a batch and leave the admin to figure out which player
    // failed. Backend would also reject but a client-side check keeps the UX
    // crisp.
    const spotsAvailable = (addPlayerSlot.max_capacity || 0) - (addPlayerSlot.registered_count || 0);
    if (selectedPlayersToAdd.length > spotsAvailable) {
      toast.error(`Only ${spotsAvailable} spot(s) available — please deselect ${selectedPlayersToAdd.length - spotsAvailable} attendee(s).`);
      return;
    }

    setAddingPlayer(true);
    try {
      // Sequential — keeps the registered_count consistent and keeps the
      // failure message attributable to the right attendee.
      const succeeded = [];
      const failed = [];
      for (const attendee of selectedPlayersToAdd) {
        try {
          // iter317 — payload differs by attendee type. `user` rows
          // (parents/customers/staff) go through the self-player resolver
          // on the backend; `player` rows use the legacy player_id field.
          const payload = { slot_id: addPlayerSlot.id, notes: 'Added by admin' };
          if (attendee.type === 'user') {
            payload.user_id = attendee.id;
          } else {
            payload.player_id = attendee.id;
          }
          await api.post('/small-group/registrations', payload);
          succeeded.push(attendee.display_name || attendee.full_name || attendee.name);
        } catch (err) {
          failed.push({
            name: attendee.display_name || attendee.full_name || attendee.name,
            reason: err.response?.data?.detail || 'Failed',
          });
        }
      }

      if (succeeded.length > 0) {
        toast.success(
          succeeded.length === 1
            ? `${succeeded[0]} has been added. They will receive an email notification.`
            : `${succeeded.length} attendees added. They will receive email notifications.`,
        );
      }
      if (failed.length > 0) {
        toast.error(
          failed.map((f) => `${f.name}: ${f.reason}`).join(' · '),
        );
      }
      if (succeeded.length > 0) {
        setAddPlayerDialog(false);
        fetchData();
      }
    } finally {
      setAddingPlayer(false);
    }
  };

  const handleRecordPayment = () => {
    // Navigate to Invoice Management page with small_group filter pre-selected
    navigate('/admin/invoice-management?type=small_group&status=pending');
    toast.info('Redirecting to Invoice Management to record payment...');
  };

  // Open Manage Players dialog
  const openManagePlayersDialog = (slot) => {
    setManagePlayersSlot(slot);
    setManagePlayersDialog(true);
  };

  // Remove a player from slot - with confirmation for attended players
  const handleRemovePlayer = async (registration) => {
    // If player has attended, show confirmation dialog first
    if (registration.attended) {
      setPlayerToRemove(registration);
      setRemoveAttendedConfirmDialog(true);
      return;
    }
    
    // Otherwise proceed directly
    await executeRemovePlayer(registration);
  };
  
  // Actually execute the player removal
  const executeRemovePlayer = async (registration) => {
    setRemovingPlayer(registration.id);
    try {
      await api.delete(`/small-group/admin/registrations/${registration.id}`);
      toast.success(`${registration.player_name} has been removed from this slot`);
      
      // Update the local managePlayersSlot state immediately
      // by filtering out the removed registration
      if (managePlayersSlot) {
        const updatedRegistrations = managePlayersSlot.registrations?.filter(
          r => r.id !== registration.id
        ) || [];
        const activeRegistrations = updatedRegistrations.filter(r => r.status !== 'cancelled');
        const attendedRegistrations = activeRegistrations.filter(r => r.attended);
        
        setManagePlayersSlot({
          ...managePlayersSlot,
          registrations: updatedRegistrations,
          registered_count: activeRegistrations.length,
          attended_count: attendedRegistrations.length
        });
      }
      
      // Also refresh the main data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove player');
    } finally {
      setRemovingPlayer(null);
    }
  };
  
  // Confirm removal of attended player
  const confirmRemoveAttendedPlayer = async () => {
    if (playerToRemove) {
      setRemoveAttendedConfirmDialog(false);
      await executeRemovePlayer(playerToRemove);
      setPlayerToRemove(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      full: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-slate-100 text-slate-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
      sent: 'bg-blue-100 text-blue-800'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  // Helper to get coach name for a slot
  const getCoachNameForSlot = (slot) => {
    const session = sessions.find(s => s.id === slot.session_id);
    if (!session) return 'Unknown';
    return session.coach_name || coaches.find(c => c.id === session.coach_id)?.name || 'Unknown';
  };

  // Sorting function
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredSlots = slots.filter(slot => {
    if (sessionFilter && slot.session_id !== sessionFilter) return false;
    if (dateFilter && slot.date !== dateFilter) return false;
    return true;
  }).sort((a, b) => {
    let aVal, bVal;
    
    switch (sortColumn) {
      case 'date':
        aVal = a.date;
        bVal = b.date;
        break;
      case 'session':
        aVal = sessions.find(s => s.id === a.session_id)?.name || '';
        bVal = sessions.find(s => s.id === b.session_id)?.name || '';
        break;
      case 'coach':
        aVal = getCoachNameForSlot(a);
        bVal = getCoachNameForSlot(b);
        break;
      case 'time':
        aVal = a.start_time;
        bVal = b.start_time;
        break;
      case 'registered':
        aVal = a.registered_count || 0;
        bVal = b.registered_count || 0;
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      default:
        aVal = a[sortColumn];
        bVal = b[sortColumn];
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    // Tie-breakers — when sorting by date, fall back to start_time (and vice
    // versa) so same-day rows always show in chronological order.
    if (sortColumn === 'date') {
      const aTime = a.start_time || '';
      const bTime = b.start_time || '';
      if (aTime < bTime) return sortDirection === 'asc' ? -1 : 1;
      if (aTime > bTime) return sortDirection === 'asc' ? 1 : -1;
    } else if (sortColumn === 'time') {
      const aDate = a.date || '';
      const bDate = b.date || '';
      if (aDate < bDate) return sortDirection === 'asc' ? -1 : 1;
      if (aDate > bDate) return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Small Group Sessions</h1>
            <p className="text-slate-500">Manage private coaching sessions with dynamic pricing</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{sessions.length}</p>
                  <p className="text-sm text-slate-500">Active Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{slots.filter(s => s.status === 'open').length}</p>
                  <p className="text-sm text-slate-500">Open Slots</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{slots.reduce((acc, s) => acc + (s.registered_count || 0), 0)}</p>
                  <p className="text-sm text-slate-500">Total Registrations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {cs()}{invoices.filter(i => i.status === 'pending').reduce((acc, i) => acc + i.total_amount, 0).toFixed(0)}
                  </p>
                  <p className="text-sm text-slate-500">Pending Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full overflow-x-auto">
            <TabsTrigger value="sessions" className="text-xs lg:text-sm whitespace-nowrap">Sessions</TabsTrigger>
            <TabsTrigger value="slots" className="text-xs lg:text-sm whitespace-nowrap">Slots & Attendance</TabsTrigger>
            <TabsTrigger value="pricing" className="text-xs lg:text-sm whitespace-nowrap">Coach Pricing</TabsTrigger>
            <TabsTrigger value="invoices" className="text-xs lg:text-sm whitespace-nowrap">Invoices</TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Session Templates</h2>
              <Button onClick={() => { resetSessionForm(); setSessionDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" /> New Session
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="sg-sessions-grid">
              {sessions.length === 0 ? (
                <Card className="md:col-span-2 xl:col-span-3">
                  <CardContent className="py-12 text-center text-slate-500">
                    No sessions yet. Create your first session template.
                  </CardContent>
                </Card>
              ) : (
                sessions.map(session => (
                  <Card key={session.id} className="h-full" data-testid={`sg-session-card-${session.id}`}>
                    <CardContent className="pt-6 h-full">
                      <div className="flex flex-col gap-4 h-full justify-between">
                        <div className="space-y-1 min-w-0">
                          <h3 className="font-semibold text-lg">{session.name}</h3>
                          <p className="text-sm text-slate-500">
                            Coach: {session.coach_name || 'Not assigned'}
                          </p>
                          {session.description && (
                            <p className="text-sm text-slate-600">{session.description}</p>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-2">
                            {session.recurring_day && (
                              <span className="capitalize">Every {session.recurring_day}</span>
                            )}
                            {session.default_start_time && (
                              <span>{session.default_start_time}</span>
                            )}
                            <span>Max {session.max_capacity} kids</span>
                            {session.location_id && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {locations.find(l => l.id === session.location_id)?.name || session.location || 'Unknown'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Badge variant={session.is_active ? 'default' : 'secondary'} className="mr-auto">
                            {session.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingSession(session);
                              setSessionForm({
                                name: session.name,
                                coach_id: session.coach_id,
                                description: session.description || '',
                                recurring_day: session.recurring_day || '',
                                default_start_time: session.default_start_time || '',
                                default_duration_hours: session.default_duration_hours || 2,
                                max_capacity: session.max_capacity || 10,
                                location_id: session.location_id || ''
                              });
                              setSessionDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteSession(session.id, session.name)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Slots Tab */}
          <TabsContent value="slots" className="space-y-4">
            <div className="flex justify-between items-center gap-4">
              <div className="flex gap-4">
                <Select value={sessionFilter || 'all'} onValueChange={(v) => setSessionFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by session" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sessions</SelectItem>
                    {sessions.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-48"
                />
                {dateFilter && (
                  <Button variant="ghost" size="sm" onClick={() => setDateFilter('')}>
                    Clear
                  </Button>
                )}
              </div>
              <Button onClick={() => { resetSlotForm(); setSlotDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" /> New Slot
              </Button>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('session')}
                    >
                      <div className="flex items-center gap-1">
                        Session
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('coach')}
                    >
                      <div className="flex items-center gap-1">
                        Coach
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('time')}
                    >
                      <div className="flex items-center gap-1">
                        Time
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      </div>
                    </TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Lane</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('registered')}
                    >
                      <div className="flex items-center gap-1">
                        Registered
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSlots.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No slots found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSlots.map(slot => (
                      <TableRow key={slot.id}>
                        <TableCell className="font-medium">
                          {format(parseISO(slot.date), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>{slot.session_name}</TableCell>
                        <TableCell className="text-slate-600">{getCoachNameForSlot(slot)}</TableCell>
                        <TableCell>{slot.start_time} - {slot.end_time}</TableCell>
                        <TableCell>{slot.duration_hours}h</TableCell>
                        <TableCell>
                          {(() => {
                            const names = (slot.lane_names && slot.lane_names.length)
                              ? slot.lane_names
                              : (slot.lane_name ? [slot.lane_name] : []);
                            if (names.length === 0) return <span className="text-slate-400">-</span>;
                            return (
                              <div className="flex flex-wrap gap-1" data-testid={`slot-lanes-${slot.id}`}>
                                {names.map((n, i) => (
                                  <Badge key={i} variant="outline" className="bg-blue-50">{n}</Badge>
                                ))}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {slot.registered_count} / {slot.max_capacity}
                          {slot.attended_count > 0 && (
                            <span className="text-green-600 ml-2">
                              ({slot.attended_count} attended)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(slot.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {/* Manage Players - only for open, in_progress, full statuses */}
                            {['open', 'in_progress', 'full'].includes(slot.status) && slot.registered_count > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openManagePlayersDialog(slot)}
                                title="Manage registered players"
                                data-testid={`manage-players-btn-${slot.id}`}
                              >
                                <Users className="h-4 w-4 text-purple-600" />
                              </Button>
                            )}
                            {slot.status !== 'completed' && slot.registered_count < slot.max_capacity && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openAddPlayerDialog(slot)}
                                title="Add Player"
                              >
                                <UserPlus className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openAttendanceDialog(slot)}
                              disabled={slot.registered_count === 0}
                              title={slot.status === 'completed' ? "View Attendance (Read-only)" : "Mark Attendance"}
                            >
                              {slot.status === 'completed' ? (
                                <Eye className="h-4 w-4 text-slate-500" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </Button>
                            {slot.status !== 'completed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCompleteSlot(slot)}
                                title="Complete slot and generate invoices"
                              >
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {slot.status === 'completed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReopenSlot(slot)}
                                title="Reopen slot to modify attendance (Admin only)"
                                data-testid={`reopen-slot-${slot.id}`}
                                className="hover:bg-amber-50 hover:border-amber-400 cursor-pointer"
                              >
                                <RotateCcw className="h-4 w-4 text-amber-600" />
                              </Button>
                            )}
                            {slot.status !== 'completed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const url = `${window.location.origin}/register/small-group/${slot.id}`;
                                  navigator.clipboard?.writeText(url).catch(() => {});
                                  toast.success('Registration link copied — share it with parents/customers');
                                }}
                                title="Copy shareable registration link"
                                data-testid={`copy-register-link-${slot.id}`}
                              >
                                <LinkIcon className="h-4 w-4 text-violet-600" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopySlot(slot)}
                              title="Copy slot to create new"
                              data-testid={`copy-slot-${slot.id}`}
                            >
                              <Copy className="h-4 w-4 text-purple-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingSlot(slot);
                                setSlotForm({
                                  session_id: slot.session_id,
                                  date: slot.date,
                                  start_time: slot.start_time,
                                  end_time: slot.end_time,
                                  max_capacity: slot.max_capacity,
                                  lane_id: slot.lane_id || '', lane_ids: slot.lane_ids || (slot.lane_id ? [slot.lane_id] : []),
                                  notes: slot.notes || ''
                                });
                                setSlotDialog(true);
                              }}
                              title="Edit slot"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteSlot(slot)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Coach Pricing Grids</h2>
              <Button onClick={() => {
                setSelectedCoachForPricing('');
                setPricingGrid([]);
                setEditingPricing(null);
                setPricingDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" /> Set Coach Pricing
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {coachPricing.length === 0 ? (
                <Card className="col-span-2">
                  <CardContent className="py-12 text-center text-slate-500">
                    No pricing configurations yet. Set up pricing for your coaches.
                  </CardContent>
                </Card>
              ) : (
                coachPricing.map(pricing => (
                  <Card key={pricing.id}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{pricing.coach_name}</CardTitle>
                          <CardDescription>
                            {pricing.pricing_grid?.length || 0} pricing rules
                          </CardDescription>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCoachForPricing(pricing.coach_id);
                            setPricingGrid(pricing.pricing_grid || []);
                            setEditingPricing(pricing);
                            setPricingDialog(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs"># Kids</TableHead>
                              <TableHead className="text-xs">Duration</TableHead>
                              <TableHead className="text-xs text-right">Rate/Child</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(pricing.pricing_grid || []).slice(0, 6).map((entry, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="py-1">{entry.num_kids}</TableCell>
                                <TableCell className="py-1">{entry.duration_hours}h</TableCell>
                                <TableCell className="py-1 text-right">{cs()}{entry.rate_per_child}</TableCell>
                              </TableRow>
                            ))}
                            {(pricing.pricing_grid || []).length > 6 && (
                              <TableRow>
                                <TableCell colSpan={3} className="text-center text-sm text-slate-500">
                                  +{pricing.pricing_grid.length - 6} more rules
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Players</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No invoices yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map(invoice => (
                      <TableRow key={invoice.id}>
                        <TableCell>{invoice.slot_date}</TableCell>
                        <TableCell>{invoice.session_name}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{invoice.parent_name}</p>
                            <p className="text-sm text-slate-500">{invoice.parent_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{(invoice.player_names || []).join(', ')}</TableCell>
                        <TableCell className="text-sm">
                          <p>{invoice.num_kids} kids x {invoice.duration_hours}h</p>
                          <p className="text-slate-500">{cs()}{invoice.rate_per_child}/child</p>
                        </TableCell>
                        <TableCell className="font-semibold">{cs()}{invoice.total_amount?.toFixed(2)}</TableCell>
                        <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {invoice.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleRecordPayment()}
                              >
                                Record Payment
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDiscountInvoice(invoice)}
                              className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                              title="Apply Discount"
                              data-testid={`discount-small-group-${invoice.id}`}
                            >
                              <TicketPercent className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Session Dialog */}
        <Dialog open={sessionDialog} onOpenChange={setSessionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSession ? 'Edit Session' : 'Create Session'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Session Name *</Label>
                <Input
                  value={sessionForm.name}
                  onChange={(e) => setSessionForm({...sessionForm, name: e.target.value})}
                  placeholder="e.g., Saturday Morning Group"
                />
              </div>
              <div>
                <Label>Coach *</Label>
                <Select
                  value={sessionForm.coach_id}
                  onValueChange={(v) => setSessionForm({...sessionForm, coach_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select coach" />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={sessionForm.description}
                  onChange={(e) => setSessionForm({...sessionForm, description: e.target.value})}
                  placeholder="Session description..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Recurring Day</Label>
                  <Select
                    value={sessionForm.recurring_day || 'none'}
                    onValueChange={(v) => setSessionForm({...sessionForm, recurring_day: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="monday">Monday</SelectItem>
                      <SelectItem value="tuesday">Tuesday</SelectItem>
                      <SelectItem value="wednesday">Wednesday</SelectItem>
                      <SelectItem value="thursday">Thursday</SelectItem>
                      <SelectItem value="friday">Friday</SelectItem>
                      <SelectItem value="saturday">Saturday</SelectItem>
                      <SelectItem value="sunday">Sunday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Default Start Time</Label>
                  <Input
                    type="time"
                    value={sessionForm.default_start_time}
                    onChange={(e) => setSessionForm({...sessionForm, default_start_time: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Duration (hours)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={sessionForm.default_duration_hours}
                    onChange={(e) => setSessionForm({...sessionForm, default_duration_hours: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Max Capacity</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={sessionForm.max_capacity}
                    onChange={(e) => setSessionForm({...sessionForm, max_capacity: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <div>
                <Label>Location *</Label>
                <Select
                  value={sessionForm.location_id}
                  onValueChange={(v) => setSessionForm({...sessionForm, location_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        <span className="flex items-center gap-2">
                          <MapPin className="h-3 w-3" />
                          {loc.name} {loc.is_primary && '(Primary)'}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSessionDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveSession} disabled={!sessionForm.name || !sessionForm.coach_id}>
                {editingSession ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Slot Dialog */}
        <Dialog open={slotDialog} onOpenChange={setSlotDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSlot ? 'Edit Slot' : 'Create Slot'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Session *</Label>
                <Select
                  value={slotForm.session_id}
                  onValueChange={(v) => {
                    const session = sessions.find(s => s.id === v);
                    setSlotForm({
                      ...slotForm,
                      session_id: v,
                      max_capacity: session?.max_capacity || 10,
                      start_time: session?.default_start_time || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select session" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessions.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={slotForm.date}
                  data-testid="slot-form-date"
                  onChange={(e) => setSlotForm({...slotForm, date: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={slotForm.start_time}
                    onChange={(e) => setSlotForm({...slotForm, start_time: e.target.value})}
                  />
                </div>
                <div>
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={slotForm.end_time}
                    onChange={(e) => setSlotForm({...slotForm, end_time: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Max Capacity</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={slotForm.max_capacity}
                  onChange={(e) => setSlotForm({...slotForm, max_capacity: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Assign Lanes (Optional)</Label>
                <div className="mt-1 rounded-md border border-slate-200 p-2 max-h-40 overflow-y-auto space-y-1" data-testid="sg-lane-multiselect">
                  {lanes.length === 0 ? (
                    <p className="text-xs text-slate-400 px-1 py-1">No lanes configured.</p>
                  ) : lanes.map(lane => {
                    const selected = (slotForm.lane_ids || []).includes(lane.id);
                    return (
                      <label
                        key={lane.id}
                        className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer transition-colors ${selected ? 'bg-violet-50 text-violet-900' : 'hover:bg-slate-50'}`}
                        data-testid={`sg-lane-option-${lane.id}`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-violet-600"
                          checked={selected}
                          onChange={(e) => {
                            const cur = slotForm.lane_ids || [];
                            const next = e.target.checked ? [...cur, lane.id] : cur.filter((x) => x !== lane.id);
                            setSlotForm({ ...slotForm, lane_ids: next, lane_id: next[0] || '' });
                          }}
                        />
                        {lane.name}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Select one or more lanes to block during this time
                </p>
              </div>
              <div className="hidden">
                <Select
                  value={slotForm.lane_id || 'none'}
                  onValueChange={(v) => setSlotForm({...slotForm, lane_id: v === 'none' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select lane to block" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Lane</SelectItem>
                    {lanes.map(lane => (
                      <SelectItem key={lane.id} value={lane.id}>{lane.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Assigning a lane will block it from other bookings during this time
                </p>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={slotForm.notes}
                  onChange={(e) => setSlotForm({...slotForm, notes: e.target.value})}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSlotDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleSaveSlot} 
                data-testid="slot-form-save"
                disabled={!slotForm.session_id || !slotForm.date || !slotForm.start_time || !slotForm.end_time}
              >
                {editingSlot ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pricing Dialog */}
        <Dialog open={pricingDialog} onOpenChange={setPricingDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5" />
                  {editingPricing ? 'Edit Pricing Grid' : 'Set Coach Pricing Grid'}
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Coach *</Label>
                <Select
                  value={selectedCoachForPricing}
                  onValueChange={setSelectedCoachForPricing}
                  disabled={!!editingPricing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select coach" />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches.filter(c => 
                      editingPricing || !coachPricing.some(p => p.coach_id === c.id)
                    ).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base">Pricing Rules</Label>
                  <Button size="sm" variant="outline" onClick={addPricingRow}>
                    <Plus className="h-4 w-4 mr-1" /> Add Row
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead># of Kids</TableHead>
                      <TableHead>Duration (hrs)</TableHead>
                      <TableHead>Rate per Child ($)</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingGrid.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                          No pricing rules. Click "Add Row" to start.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pricingGrid.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Input
                              type="number"
                              min={1}
                              max={20}
                              value={row.num_kids}
                              onChange={(e) => updatePricingRow(idx, 'num_kids', e.target.value)}
                              className="w-20"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0.5}
                              max={10}
                              step="0.5"
                              value={row.duration_hours}
                              onChange={(e) => updatePricingRow(idx, 'duration_hours', e.target.value)}
                              className="w-20"
                              data-testid={`pricing-grid-duration-${idx}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              value={row.rate_per_child}
                              onChange={(e) => updatePricingRow(idx, 'rate_per_child', e.target.value)}
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removePricingRow(idx)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPricingDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleSavePricing} 
                disabled={!selectedCoachForPricing || pricingGrid.length === 0}
              >
                Save Pricing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Attendance Dialog */}
        <Dialog open={attendanceDialog} onOpenChange={setAttendanceDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {selectedSlot?.status === 'completed' ? 'View Attendance' : 'Mark Attendance'}
              </DialogTitle>
            </DialogHeader>
            {selectedSlot && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-3 rounded-lg text-sm">
                  <p><strong>Session:</strong> {selectedSlot.session_name}</p>
                  <p><strong>Date:</strong> {selectedSlot.date}</p>
                  <p><strong>Time:</strong> {selectedSlot.start_time} - {selectedSlot.end_time}</p>
                  {selectedSlot.status === 'completed' && (
                    <p className="mt-2 text-amber-600 font-medium">
                      This session is completed - attendance is view-only
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  {attendanceData.map((item, idx) => (
                    <div key={item.registration_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{item.player_name}</p>
                        <p className="text-sm text-slate-500">{item.parent_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedSlot.status === 'completed' ? (
                          <span className={`px-2 py-1 rounded text-sm ${item.attended ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {item.attended ? 'Attended' : 'Did not attend'}
                          </span>
                        ) : (
                          <>
                            <Checkbox
                              checked={item.attended}
                              onCheckedChange={(checked) => {
                                const newData = [...attendanceData];
                                newData[idx].attended = checked;
                                setAttendanceData(newData);
                              }}
                            />
                            <span className={item.attended ? 'text-green-600' : 'text-slate-400'}>
                              {item.attended ? 'Attended' : 'Not marked'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAttendanceDialog(false)}>
                {selectedSlot?.status === 'completed' ? 'Close' : 'Cancel'}
              </Button>
              {selectedSlot?.status !== 'completed' && (
                <Button onClick={handleSaveAttendance}>Save Attendance</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Player to Slot Dialog */}
        <Dialog open={addPlayerDialog} onOpenChange={setAddPlayerDialog}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Attendees to Session</DialogTitle>
            </DialogHeader>
            {addPlayerSlot && (() => {
              const spotsAvailable = (addPlayerSlot.max_capacity || 0) - (addPlayerSlot.registered_count || 0);
              const overCapacity = selectedPlayersToAdd.length > spotsAvailable;
              return (
                <div className="space-y-4 py-4">
                  <div className="bg-slate-50 p-3 rounded-lg text-sm">
                    <p><strong>Session:</strong> {sessions.find(s => s.id === addPlayerSlot.session_id)?.name || 'Unknown'}</p>
                    <p><strong>Date:</strong> {addPlayerSlot.date}</p>
                    <p><strong>Time:</strong> {addPlayerSlot.start_time} - {addPlayerSlot.end_time}</p>
                    <p><strong>Spots Available:</strong> {spotsAvailable}</p>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:underline"
                      onClick={() => {
                        const url = `${window.location.origin}/register/small-group/${addPlayerSlot.id}`;
                        navigator.clipboard?.writeText(url).catch(() => {});
                        toast.success('Registration link copied — share it with parents/customers');
                      }}
                      data-testid="sg-copy-register-link"
                    >
                      <Share2 className="h-3.5 w-3.5" /> Copy shareable registration link
                    </button>
                  </div>

                  <div className="space-y-2">
                    <Label>Search Attendee</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search players, parents, or customers..."
                        value={playerSearchQuery}
                        onChange={(e) => {
                          setPlayerSearchQuery(e.target.value);
                          searchPlayers(e.target.value);
                        }}
                        className="pl-10"
                        data-testid="add-player-search-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Tip: include parents / customers to register them as self-attendees (1:1 / adult coaching).</p>
                  </div>

                  {searchedPlayers.length > 0 && (
                    <div className="border rounded-lg max-h-56 overflow-y-auto overflow-x-hidden divide-y">
                      {searchedPlayers.map((attendee) => {
                        const checked = selectedPlayersToAdd.some((p) => p.id === attendee.id && p.type === attendee.type);
                        const isUser = attendee.type === 'user';
                        return (
                          <label
                            key={`${attendee.type}-${attendee.id}`}
                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 ${
                              checked ? 'bg-blue-50' : ''
                            }`}
                            data-testid={`add-player-row-${attendee.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => togglePlayerToAdd(attendee)}
                              className="shrink-0"
                              data-testid={`add-player-checkbox-${attendee.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium truncate">{attendee.display_name}</p>
                                {isUser ? (
                                  <Badge variant="outline" className="bg-purple-50 border-purple-300 text-purple-700 text-xs" data-testid={`attendee-badge-user-${attendee.id}`}>
                                    {(attendee.role || 'user').charAt(0).toUpperCase() + (attendee.role || 'user').slice(1)} (self)
                                  </Badge>
                                ) : attendee.is_self_player ? (
                                  <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700 text-xs">Adult self</Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-700 text-xs">Player</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 truncate">
                                {isUser
                                  ? `${attendee.email || 'No email'}${attendee.phone ? ` • ${attendee.phone}` : ''}`
                                  : `Parent: ${attendee.parent_name || 'N/A'} • ${attendee.parent_email || 'No email'}`}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {playerSearchQuery.length >= 2 && searchedPlayers.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No attendees found</p>
                  )}

                  {selectedPlayersToAdd.length > 0 && (
                    <div
                      className={`p-3 rounded-lg border ${
                        overCapacity
                          ? 'bg-rose-50 border-rose-200'
                          : 'bg-green-50 border-green-200'
                      }`}
                      data-testid="add-player-selection-summary"
                    >
                      <div className="flex items-center justify-between">
                        <p className={`font-medium ${overCapacity ? 'text-rose-800' : 'text-green-800'}`}>
                          {selectedPlayersToAdd.length} attendee{selectedPlayersToAdd.length === 1 ? '' : 's'} selected
                          {overCapacity ? ` — ${selectedPlayersToAdd.length - spotsAvailable} over capacity` : ''}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedPlayersToAdd([])}
                          className={overCapacity ? 'text-rose-700' : 'text-green-700'}
                          data-testid="add-player-clear-selection"
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedPlayersToAdd.map((p) => (
                          <Badge
                            key={`${p.type}-${p.id}`}
                            variant="outline"
                            className={overCapacity
                              ? 'bg-white border-rose-300 text-rose-700'
                              : 'bg-white border-green-300 text-green-700'}
                          >
                            {p.display_name || p.full_name || p.name}
                            <button
                              type="button"
                              className="ml-1 opacity-60 hover:opacity-100"
                              onClick={(e) => { e.preventDefault(); togglePlayerToAdd(p); }}
                              aria-label={`Remove ${p.display_name || p.full_name || p.name}`}
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                      {!overCapacity && (
                        <p className="text-sm text-green-600 mt-2">
                          Selected attendees will each receive an email notification.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddPlayerDialog(false)}>Cancel</Button>
              <Button
                onClick={handleAddPlayerToSlot}
                disabled={
                  selectedPlayersToAdd.length === 0
                  || addingPlayer
                  || (addPlayerSlot
                    && selectedPlayersToAdd.length
                      > (addPlayerSlot.max_capacity || 0) - (addPlayerSlot.registered_count || 0))
                }
                data-testid="add-players-confirm-btn"
              >
                {addingPlayer ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {selectedPlayersToAdd.length > 1
                      ? `Add ${selectedPlayersToAdd.length} Attendees`
                      : 'Add Attendee'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Slot Confirmation Dialog */}
        <Dialog open={completeConfirmDialog} onOpenChange={setCompleteConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Complete Session
              </DialogTitle>
              <DialogDescription className="pt-2">
                {completeConfirmMessage}
              </DialogDescription>
            </DialogHeader>
            {completeSlotData && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Session:</strong> {sessions.find(s => s.id === completeSlotData.session_id)?.name || 'Unknown'}</p>
                <p><strong>Date:</strong> {completeSlotData.date}</p>
                <p><strong>Time:</strong> {completeSlotData.start_time} - {completeSlotData.end_time}</p>
                <p><strong>Attended:</strong> {completeSlotData.attended_count} of {completeSlotData.registered_count} registered</p>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setCompleteConfirmDialog(false);
                  setCompleteSlotData(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmCompleteSlot}
                disabled={completingSlot}
                className="bg-green-600 hover:bg-green-700"
              >
                {completingSlot ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Complete & Generate Invoices
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Post-Attendance Prompt Dialog */}
        <Dialog open={postAttendanceDialog} onOpenChange={setPostAttendanceDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Attendance Saved!
              </DialogTitle>
              <DialogDescription className="pt-2">
                {postAttendanceCount} player(s) marked as attended. Would you like to complete this session and generate invoices now?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setPostAttendanceDialog(false);
                  setPostAttendanceSlot(null);
                }}
              >
                Not Now
              </Button>
              <Button 
                onClick={handlePostAttendanceComplete}
                disabled={completingSlot}
                className="bg-green-600 hover:bg-green-700"
              >
                {completingSlot ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Complete & Invoice
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reopen Slot Confirmation Dialog */}
        <Dialog open={reopenConfirmDialog} onOpenChange={setReopenConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Reopen Completed Slot?
              </DialogTitle>
              <DialogDescription className="pt-2">
                Are you sure you want to reopen this completed slot? This will allow attendance to be modified and will delete any pending (unpaid) invoices for this slot.
              </DialogDescription>
            </DialogHeader>
            {reopenSlotData && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Session:</strong> {reopenSlotData.session_name}</p>
                <p><strong>Date:</strong> {reopenSlotData.date}</p>
                <p><strong>Time:</strong> {reopenSlotData.start_time} - {reopenSlotData.end_time}</p>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setReopenConfirmDialog(false);
                  setReopenSlotData(null);
                }}
                disabled={reopeningSlot}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmReopenSlot}
                disabled={reopeningSlot}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {reopeningSlot ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reopening...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reopen Slot
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Confirm Delete
              </DialogTitle>
              <DialogDescription className="pt-2">
                Are you sure you want to delete <strong>{deleteItemName}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDeleteConfirmDialog(false);
                  setDeleteItemId(null);
                  setDeleteType(null);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete}
                disabled={deleting}
                variant="destructive"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove Attended Player Confirmation Dialog */}
        <Dialog open={removeAttendedConfirmDialog} onOpenChange={setRemoveAttendedConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Remove Attended Player?
              </DialogTitle>
              <DialogDescription className="pt-2">
                <strong>{playerToRemove?.player_name}</strong> has already been marked as <strong className="text-green-600">attended</strong> for this session. 
                Removing them may affect billing and attendance records.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
              <p className="font-medium">Warning:</p>
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li>This player's attendance will be removed</li>
                <li>Any pending invoices may need to be adjusted</li>
                <li>The attended count will be updated</li>
              </ul>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setRemoveAttendedConfirmDialog(false);
                  setPlayerToRemove(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmRemoveAttendedPlayer}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                Yes, Remove Player
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Players Dialog */}
        <Dialog open={managePlayersDialog} onOpenChange={setManagePlayersDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="manage-players-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Manage Registered Players
              </DialogTitle>
              <DialogDescription>
                {managePlayersSlot?.session_name} - {managePlayersSlot?.date}
              </DialogDescription>
            </DialogHeader>
            
            {managePlayersSlot && (
              <div className="space-y-4 py-2">
                <div className="flex justify-between items-center text-sm text-slate-500">
                  <span>Time: {managePlayersSlot.start_time} - {managePlayersSlot.end_time}</span>
                  <span>Status: {getStatusBadge(managePlayersSlot.status)}</span>
                </div>
                
                <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto" data-testid="registered-players-list">
                  {managePlayersSlot.registrations?.filter(r => r.status !== 'cancelled').length > 0 ? (
                    managePlayersSlot.registrations
                      .filter(r => r.status !== 'cancelled')
                      .map((reg) => (
                        <div key={reg.id} className="flex items-center justify-between p-3 hover:bg-slate-50" data-testid={`player-row-${reg.id}`}>
                          <div>
                            <p className="font-medium" data-testid={`player-name-${reg.id}`}>{reg.player_name}</p>
                            <p className="text-sm text-slate-500">{reg.parent_name}</p>
                            <div className="flex gap-1 mt-1">
                              {reg.attended && (
                                <Badge className="bg-green-100 text-green-800 text-xs">Attended</Badge>
                              )}
                              {reg.payment_status === 'paid' && reg.refund_status !== 'fully_refunded' && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Paid</Badge>
                              )}
                              {reg.refund_status === 'partially_refunded' && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Partial refund</Badge>
                              )}
                              {reg.refund_status === 'fully_refunded' && (
                                <Badge className="bg-slate-200 text-slate-700 text-xs">Refunded</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {reg.payment_status === 'paid' && reg.refund_status !== 'fully_refunded' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setRefundReg(reg); setRefundOpen(true); }}
                                className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                title="Issue refund"
                                data-testid={`refund-coach-${reg.id}`}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemovePlayer(reg)}
                              disabled={removingPlayer === reg.id}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Remove player from slot"
                              data-testid={`remove-player-btn-${reg.id}`}
                            >
                              {removingPlayer === reg.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <UserMinus className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="p-4 text-center text-slate-500" data-testid="no-players-message">
                      No players registered for this slot
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-slate-500">
                  Removing a player will cancel their registration. You can add new players using the "+" button in the slots table.
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setManagePlayersDialog(false)} data-testid="close-manage-players-btn">
                Close
              </Button>
              <Button
                onClick={() => {
                  setManagePlayersDialog(false);
                  if (managePlayersSlot) {
                    openAddPlayerDialog(managePlayersSlot);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-player-from-manage-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Player
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        entityType="coaching_session"
        entity={refundReg}
        priceField="amount"
        summaryLines={
          refundReg
            ? [
                { label: 'Player', value: refundReg.player_name || '—' },
                { label: 'Parent', value: refundReg.parent_name || '—' },
              ]
            : []
        }
        allowCancelCheckbox={false}
        onRefunded={() => { setRefundOpen(false); setRefundReg(null); fetchData(); }}
      />

      <ManualDiscountDialog
        open={!!discountInvoice}
        onOpenChange={(v) => !v && setDiscountInvoice(null)}
        transactionType="small_group_invoice"
        transactionId={discountInvoice?.id}
        onChange={() => fetchData()}
      />
    </DashboardLayout>
  );
};

export default SmallGroupManagement;
